import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import SyncIcon from '@mui/icons-material/Sync';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import useHealth, { sanitizeWabaNumber } from '../../hooks/useHealth';
import { templateService } from '../../services/templateService';
import { mapBotToSelectOption } from '../../utils/normalizeTemplate';
import TemplateDataTable from '../shared/TemplateDataTable';
import TemplateColumnHeader from '../list/TemplateColumnHeader';
import { radiusPx } from '../../../../theme/tokens';

const CALLBACK_PRESETS = [
  {
    label: '1) Template Status Update (APPROVED)',
    payload: {
      value: {
        event: 'APPROVED',
        message_template_id: '1323041515465741',
        message_template_name: 'hello_world_qs_o8vcf7r0',
        message_template_language: 'en',
        reason: 'NONE',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'message_template_status_update',
    },
  },
  {
    label: '1) Template Status Update (REJECTED)',
    payload: {
      value: {
        event: 'REJECTED',
        message_template_id: '1323041515465741',
        message_template_name: 'hello_world_qs_o8vcf7r0',
        message_template_language: 'en',
        reason: 'INCORRECT_CATEGORY',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'message_template_status_update',
    },
  },
  {
    label: '2) Template Category Update',
    payload: {
      value: {
        message_template_id: '1244699683975669',
        message_template_name: 'hello_world_21222_p2i0bbie4mw',
        message_template_language: 'en',
        new_category: 'MARKETING',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'template_category_update',
    },
  },
  {
    label: '3) Template Quality Upgrade (GREEN)',
    payload: {
      value: {
        previous_quality_score: 'UNKNOWN',
        new_quality_score: 'GREEN',
        message_template_id: 1079366249727354,
        message_template_name: 'ihf6o5cd9wd_pickupconfirmationevent',
        message_template_language: 'en',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'message_template_quality_update',
    },
  },
  {
    label: '3) Template Quality Low Grade (YELLOW)',
    payload: {
      value: {
        previous_quality_score: 'GREEN',
        new_quality_score: 'YELLOW',
        message_template_id: 1357782831904380,
        message_template_name: 'reg_verificationotp_event_q1bflbzkia2',
        message_template_language: 'en',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'message_template_quality_update',
    },
  },
  {
    label: '4) Phone Quality: ONBOARDING (TIER_1K)',
    payload: {
      value: {
        display_phone_number: '919773336091',
        event: 'ONBOARDING',
        current_limit: 'TIER_1K',
        old_limit: 'TIER_NOT_SET',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_quality_update',
    },
  },
  {
    label: '4) Phone Quality: FLAGGED (TIER_10K)',
    payload: {
      value: {
        display_phone_number: '918826998005',
        event: 'FLAGGED',
        current_limit: 'TIER_10K',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_quality_update',
    },
  },
  {
    label: '4) Phone Quality: UNFLAGGED (TIER_100K)',
    payload: {
      value: {
        display_phone_number: '919076001190',
        event: 'UNFLAGGED',
        current_limit: 'TIER_100K',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_quality_update',
    },
  },
  {
    label: '4) Phone Quality: UPGRADE (TIER_100K)',
    payload: {
      value: {
        display_phone_number: '919717844566',
        event: 'UPGRADE',
        current_limit: 'TIER_100K',
        old_limit: 'TIER_10K',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_quality_update',
    },
  },
  {
    label: '5) Phone Name: DEFERRED',
    payload: {
      value: {
        display_phone_number: '918828827906',
        decision: 'DEFERRED',
        requested_verified_name: 'XYZ',
        rejection_reason: 'NONE',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_name_update',
    },
  },
  {
    label: '5) Phone Name: APPROVED',
    payload: {
      value: {
        display_phone_number: '919773336091',
        decision: 'APPROVED',
        requested_verified_name: 'XYZ',
        rejection_reason: 'NONE',
      },
      acid: '6690d82f6e32c38cf1b6b391e',
      webhook_type: 'phone_number_name_update',
    },
  },
  {
    label: '6) Embedded Signup: Completed',
    payload: {
      transaction_id: '5367546754456668',
      acid: '61ca8f5559a56158f75732ac',
      phone: {
        name: 'Arihant Test',
        pnum: '15557690960',
        mphid: '610206612185480',
        pquality: 'UNKNOWN',
        status: 'connected',
        limit: 'UNAVAILABLE',
        level: 'NOT_APPLICABLE',
        created: '2025-06-06T13:19:16.954Z',
        modified: '2025-06-06T13:19:16.954Z',
      },
      embeddedStatus: 'completed',
      webhook_type: 'embedded',
    },
  },
];

function QualityRatingBadge({ rating = '' }) {
  const norm = String(rating || 'UNKNOWN').toUpperCase();
  let bg = '#f5f5f5';
  let color = '#616161';
  let icon = <HelpOutlineIcon sx={{ fontSize: 16 }} />;

  if (norm.includes('GREEN') || norm === 'HIGH' || norm === 'GOOD') {
    bg = '#e8f5e9';
    color = '#2e7d32';
    icon = <CheckCircleIcon sx={{ fontSize: 16, color }} />;
  } else if (norm.includes('YELLOW') || norm === 'MEDIUM') {
    bg = '#fff8e1';
    color = '#ed6c02';
    icon = <WarningAmberIcon sx={{ fontSize: 16, color }} />;
  } else if (norm.includes('RED') || norm === 'LOW') {
    bg = '#ffebee';
    color = '#d32f2f';
    icon = <ErrorOutlineIcon sx={{ fontSize: 16, color }} />;
  }

  return (
    <Chip
      size="small"
      icon={icon}
      label={`Quality: ${norm}`}
      sx={{
        bgcolor: bg,
        color,
        fontWeight: 600,
        fontSize: '0.75rem',
        borderRadius: radiusPx.sm,
      }}
    />
  );
}

function StatusBadge({ status = '' }) {
  const norm = String(status || 'UNKNOWN').toUpperCase();
  let bg = '#f5f5f5';
  let color = '#616161';

  if (norm === 'CONNECTED' || norm === 'APPROVED' || norm === 'LIVE' || norm === 'HEALTHY') {
    bg = '#e8f5e9';
    color = '#2e7d32';
  } else if (norm === 'PENDING' || norm === 'PAUSED') {
    bg = '#fff8e1';
    color = '#ed6c02';
  } else if (
    norm === 'FLAGGED' ||
    norm === 'RESTRICTED' ||
    norm === 'RATE_LIMITED' ||
    norm === 'REJECTED' ||
    norm === 'DISABLED'
  ) {
    bg = '#ffebee';
    color = '#d32f2f';
  }

  return (
    <Chip
      size="small"
      label={norm}
      sx={{
        bgcolor: bg,
        color,
        fontWeight: 600,
        fontSize: '0.75rem',
        borderRadius: radiusPx.sm,
      }}
    />
  );
}

function RawJsonAccordion({ title = 'Raw API Response', data }) {
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  const jsonStr = JSON.stringify(data, null, 2);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(jsonStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mt: 2,
        border: '1px solid #e0e0e0',
        borderRadius: `${radiusPx.sm}px !important`,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mr: 1 }}>
          <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flex: 1 }}>
            {title}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy JSON'} arrow>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            bgcolor: '#0f172a',
            color: '#38bdf8',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            borderRadius: radiusPx.xs,
            overflowX: 'auto',
            maxHeight: 260,
          }}
        >
          {jsonStr}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function HealthPanel({
  onBack,
  initialTab = 0,
  initialWabaNumber = '',
  initialTemplateName = '',
  companyId = '',
  templates = [],
  selectedBotNumber = '',
}) {
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [wabaNumberInput, setWabaNumberInput] = useState(
    initialWabaNumber ? sanitizeWabaNumber(initialWabaNumber) : ''
  );
  const [templateNameInput, setTemplateNameInput] = useState(
    initialTemplateName || (templates[0]?.wa_Id || templates[0]?.wa_id || templates[0]?.name || '')
  );
  const [wabaIdInput, setWabaIdInput] = useState('');
  const [allSearch, setAllSearch] = useState('');
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
  const [callbackPayload, setCallbackPayload] = useState(() =>
    JSON.stringify(CALLBACK_PRESETS[0].payload, null, 2)
  );

  const [orgBotNumbers, setOrgBotNumbers] = useState([]);
  const [loadingBots, setLoadingBots] = useState(false);

  const {
    phoneHealth,
    allPhoneHealth,
    templateHealth,
    callbackState,
    fetchPhoneHealth,
    wabaMsgLimit,
    fetchWabaMsgLimit,
    fetchAllPhoneHealth,
    fetchTemplateHealth,
    triggerCallback,
  } = useHealth();

  useEffect(() => {
    let isMounted = true;
    async function loadOrgBots() {
      if (!companyId) return;
      try {
        setLoadingBots(true);
        const res = await templateService.getBotNumbers(companyId);
        const rawList = Array.isArray(res.data) ? res.data : res.data?.list ?? res.data?.results ?? [];
        const options = rawList.map(mapBotToSelectOption).filter(Boolean);
        if (isMounted) {
          setOrgBotNumbers(options);
          if (options.length > 0) {
            const detectedWaba = options.find((b) => b.waba_id)?.waba_id || '';
            if (detectedWaba) {
              setWabaIdInput((prev) => prev || detectedWaba);
              fetchWabaMsgLimit(detectedWaba);
            }
            const currentMatching = options.find((b) => b.wa_phone_number === wabaNumberInput);
            if (!currentMatching) {
              const defaultBot = selectedBotNumber
                ? options.find((b) => b.wa_phone_number === selectedBotNumber) || options[0]
                : options[0];
              const phoneToUse = defaultBot?.wa_phone_number || '';
              if (phoneToUse) {
                setWabaNumberInput(phoneToUse);
                fetchPhoneHealth(phoneToUse, companyId);
              }
              if (defaultBot?.waba_id) {
                setWabaIdInput((prev) => prev || defaultBot.waba_id);
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load organisation bot numbers', err);
      } finally {
        if (isMounted) setLoadingBots(false);
      }
    }
    loadOrgBots();
    return () => {
      isMounted = false;
    };
  }, [companyId, selectedBotNumber]);

  useEffect(() => {
    if (initialTab !== undefined) {
      setCurrentTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (templates && templates.length > 0 && !templateNameInput) {
      const first = templates[0];
      const id = first?.wa_Id || first?.wa_id || first?.name || '';
      if (id) {
        setTemplateNameInput(id);
      }
    }
  }, [templates, templateNameInput]);

  useEffect(() => {
    if (initialWabaNumber) {
      const clean = sanitizeWabaNumber(initialWabaNumber);
      setWabaNumberInput(clean);
      fetchPhoneHealth(clean, companyId);
    }
    if (initialTemplateName) {
      const match = templates.find(
        (t) =>
          t.name === initialTemplateName ||
          t.wa_Id === initialTemplateName ||
          t.wa_id === initialTemplateName
      );
      const targetId = match?.wa_Id || match?.wa_id || initialTemplateName;
      setTemplateNameInput(targetId);
      fetchTemplateHealth({
        name: match?.name || initialTemplateName,
        templateId: targetId,
        wabaNumber: match?.bot_number || (initialWabaNumber ? sanitizeWabaNumber(initialWabaNumber) : ''),
        companyId,
      });
    }
  }, [initialWabaNumber, initialTemplateName, companyId, fetchPhoneHealth, fetchTemplateHealth]);

  useEffect(() => {
    const matchingBot = orgBotNumbers.find((b) => b.wa_phone_number === wabaNumberInput);
    const targetWabaId =
      matchingBot?.waba_id ||
      phoneHealth.data?.wabaId ||
      wabaIdInput ||
      orgBotNumbers.find((b) => b.waba_id)?.waba_id;
    if (targetWabaId && (!wabaMsgLimit.data || wabaMsgLimit.data.wabaId !== targetWabaId)) {
      fetchWabaMsgLimit(targetWabaId);
    }
  }, [wabaNumberInput, orgBotNumbers, phoneHealth.data?.wabaId, wabaIdInput, fetchWabaMsgLimit, wabaMsgLimit.data]);


  const handleCheckPhoneHealth = (num) => {
    const target = num || wabaNumberInput;
    if (target) {
      fetchPhoneHealth(target, companyId);
      const matchingBot = orgBotNumbers.find((b) => b.wa_phone_number === target);
      const targetWabaId = matchingBot?.waba_id || wabaIdInput;
      if (targetWabaId) {
        fetchWabaMsgLimit(targetWabaId);
      }
    }
  };

  const handleFetchAll = () => {
    const targetWabaId = wabaIdInput.trim() || orgBotNumbers.find((b) => b.waba_id)?.waba_id || '';
    if (targetWabaId) {
      fetchAllPhoneHealth(targetWabaId, companyId);
    }
  };

  const handleCheckTemplateHealth = (nameOrId) => {
    const target = nameOrId || templateNameInput;
    if (target || wabaNumberInput) {
      const matchingTpl = templates.find(
        (t) =>
          (t.wa_Id && t.wa_Id === target) ||
          (t.wa_id && t.wa_id === target) ||
          t.name === target ||
          t.id === target
      );
      // Priority 1: wa_Id / wa_id (TrustSignal template ID key)
      const effectiveTemplateId =
        matchingTpl?.wa_Id ||
        matchingTpl?.wa_id ||
        target;

      fetchTemplateHealth({
        name: matchingTpl?.name || target,
        templateId: effectiveTemplateId,
        wabaNumber: matchingTpl?.bot_number || wabaNumberInput,
        companyId,
      });
    }
  };

  const handleSelectCallbackPreset = (idx) => {
    setSelectedPresetIndex(idx);
    const chosen = CALLBACK_PRESETS[idx];
    if (chosen) {
      setCallbackPayload(JSON.stringify(chosen.payload, null, 2));
    }
  };

  const handleTriggerCallback = () => {
    let parsed = {};
    try {
      parsed = JSON.parse(callbackPayload);
    } catch {
      parsed = { raw_payload: callbackPayload };
    }
    triggerCallback(parsed);
  };

  const filteredAllNumbers = useMemo(() => {
    if (!allPhoneHealth.data) return [];
    if (!allSearch.trim()) return allPhoneHealth.data;
    const s = allSearch.trim().toLowerCase();
    return allPhoneHealth.data.filter(
      (item) =>
        item.phoneNumber?.toLowerCase().includes(s) ||
        item.verifiedName?.toLowerCase().includes(s) ||
        item.status?.toLowerCase().includes(s) ||
        item.qualityRating?.toLowerCase().includes(s)
    );
  }, [allPhoneHealth.data, allSearch]);

  const renderHeader = (label) => () => <TemplateColumnHeader>{label}</TemplateColumnHeader>;

  const allColumns = useMemo(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        Header: renderHeader('S.No.'),
        enableSorting: false,
        size: 52,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: 'phoneNumber',
        header: 'Phone / WABA Number',
        Header: renderHeader('Phone / WABA Number'),
        size: 180,
        Cell: ({ row }) => (
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8125rem' }}>
            {row.original.displayPhoneNumber || row.original.phoneNumber || '—'}
          </Typography>
        ),
      },
      {
        accessorKey: 'verifiedName',
        header: 'Verified Name',
        Header: renderHeader('Verified Name'),
        size: 180,
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      {
        accessorKey: 'qualityRating',
        header: 'Quality Rating',
        Header: renderHeader('Quality Rating'),
        size: 140,
        Cell: ({ cell }) => <QualityRatingBadge rating={cell.getValue()} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Header: renderHeader('Status'),
        size: 130,
        Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
      },
      {
        accessorKey: 'messagingLimitTier',
        header: 'Tier Limit',
        Header: renderHeader('Tier Limit'),
        size: 130,
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      {
        accessorKey: 'accountMode',
        header: 'Mode',
        Header: renderHeader('Mode'),
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() || 'LIVE'}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Action',
        Header: renderHeader('Action'),
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setWabaNumberInput(sanitizeWabaNumber(row.original.phoneNumber));
              setCurrentTab(0);
              fetchPhoneHealth(row.original.phoneNumber, companyId);
            }}
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
          >
            Inspect
          </Button>
        ),
      },
    ],
    [companyId, fetchPhoneHealth]
  );

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Sub-Header bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          pb: 1.5,
          mb: 2.5,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {onBack ? (
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: radiusPx.sm,
                p: 0.75,
                color: 'text.secondary',
                '&:hover': { bgcolor: '#f1f5f9', color: '#059669' },
              }}
              title="Back to Templates"
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}
          <Box
            sx={{
              bgcolor: '#ecfdf5',
              width: 40,
              height: 40,
              borderRadius: radiusPx.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MonitorHeartIcon sx={{ color: '#059669', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              WABA & Phone Health Studio
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
              Real-time WhatsApp health verification and quality ratings
            </Typography>
          </Box>
        </Box>

        <Chip
          size="small"
          label="Live Diagnostics"
          sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, fontSize: '0.75rem' }}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={currentTab}
        onChange={(_e, v) => setCurrentTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          '& .MuiTab-root': {
            minHeight: 44,
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
            gap: 1,
          },
        }}
      >
        <Tab icon={<PhoneAndroidIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Phone Health (Single Number)" />
        <Tab icon={<TableChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="All Numbers Under WABA" />
        <Tab icon={<ArticleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Template Health" />
        {/* <Tab icon={<SyncIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Webhook & Callbacks Simulator" /> */}
      </Tabs>

      {/* Tab 0: Single Phone Health */}
      {currentTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: radiusPx.md,
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
            }}
          >
            {loadingBots ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: '#059669' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Loading organisation bot numbers…
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 380 }, flex: 1 }}>
                  <InputLabel id="org-bot-number-dropdown-label">Bot number</InputLabel>
                  <Select
                    labelId="org-bot-number-dropdown-label"
                    label="Bot number"
                    value={
                      orgBotNumbers.some((b) => b.wa_phone_number === wabaNumberInput)
                        ? wabaNumberInput
                        : (orgBotNumbers[0]?.wa_phone_number || '')
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setWabaNumberInput(val);
                      const matchingBot = orgBotNumbers.find((b) => b.wa_phone_number === val);
                      if (matchingBot?.waba_id) {
                        setWabaIdInput(matchingBot.waba_id);
                        fetchWabaMsgLimit(matchingBot.waba_id);
                      }
                      if (val) handleCheckPhoneHealth(val);
                    }}
                  >
                    {orgBotNumbers.map((bot) => (
                      <MenuItem key={bot.wa_phone_number} value={bot.wa_phone_number}>
                        {bot.label || `${bot.app_name} (${bot.wa_phone_number})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={() => handleCheckPhoneHealth()}
                  disabled={phoneHealth.loading || !wabaNumberInput.trim()}
                  startIcon={
                    phoneHealth.loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={{
                    whiteSpace: 'nowrap',
                    px: 3,
                    height: 40,
                    bgcolor: '#059669',
                    '&:hover': { bgcolor: '#047857' },
                  }}
                >
                  {phoneHealth.loading ? 'Checking…' : 'Check Phone Health'}
                </Button>
              </Box>
            )}
          </Paper>

          {phoneHealth.error && (
            <Alert severity="error" sx={{ borderRadius: radiusPx.sm }}>
              {phoneHealth.error}
            </Alert>
          )}

          {phoneHealth.data && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: radiusPx.md,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 3,
                  pb: 2.5,
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {phoneHealth.data.verifiedName || 'Business Phone'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 600 }}
                  >
                    {phoneHealth.data.displayPhoneNumber || phoneHealth.data.phoneNumber}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <QualityRatingBadge rating={phoneHealth.data.qualityRating} />
                  <StatusBadge status={phoneHealth.data.status} />
                  <Chip
                    size="small"
                    label={`Mode: ${phoneHealth.data.accountMode || 'LIVE'}`}
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: radiusPx.sm, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <SpeedIcon sx={{ fontSize: 18, color: '#0284c7' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Messaging Tier Limit
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                      {wabaMsgLimit?.loading ? (
                        <CircularProgress size={16} sx={{ color: '#0284c7' }} />
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#0284c7' }}>
                          {(wabaMsgLimit?.data?.messagingLimit && wabaMsgLimit.data.messagingLimit !== '—')
                            ? wabaMsgLimit.data.messagingLimit
                            : phoneHealth.data.messagingLimitTier || '—'}
                        </Typography>
                      )}
                      {wabaMsgLimit?.data?.status ? (
                        <Chip
                          size="small"
                          label={wabaMsgLimit.data.status}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            bgcolor: wabaMsgLimit.data.status === 'APPROVED' ? '#ecfdf5' : '#f1f5f9',
                            color: wabaMsgLimit.data.status === 'APPROVED' ? '#059669' : 'text.secondary',
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: radiusPx.sm, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <SecurityIcon sx={{ fontSize: 18, color: '#059669' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Name Status
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {phoneHealth.data.nameStatus || '—'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: radiusPx.sm, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#059669' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Connection Status
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {phoneHealth.data.status || 'CONNECTED'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: radiusPx.sm, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <PhoneAndroidIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Meta Phone ID
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                      {phoneHealth.data.id || '—'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      )}

      {/* Tab 1: All Numbers Under WABA - Using TemplateDataTable from templatelist */}
      {currentTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {orgBotNumbers.some((b) => b.waba_id) ? (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="org-waba-select-label">Select WABA</InputLabel>
                <Select
                  labelId="org-waba-select-label"
                  label="Select WABA"
                  value={wabaIdInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setWabaIdInput(val);
                    if (val) fetchAllPhoneHealth(val, companyId); fetchWabaMsgLimit(val);
                  }}
                >
                  {Array.from(new Set(orgBotNumbers.map((b) => b.waba_id).filter(Boolean))).map((wid) => {
                    const matching = orgBotNumbers.find((b) => b.waba_id === wid);
                    return (
                      <MenuItem key={wid} value={wid}>
                        {matching?.app_name ? `${matching.app_name} (${wid})` : wid}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : null}

            <TextField
              size="small"
              label="WABA ID"
              placeholder="Enter WABA ID"
              value={wabaIdInput}
              onChange={(e) => setWabaIdInput(e.target.value)}
              sx={{ width: 220 }}
            />

            <TextField
              size="small"
              placeholder="Search phone number, name or status…"
              value={allSearch}
              onChange={(e) => setAllSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />

            <Button
              variant="contained"
              onClick={handleFetchAll}
              disabled={allPhoneHealth.loading || !wabaIdInput.trim()}
              startIcon={
                allPhoneHealth.loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, height: 40, whiteSpace: 'nowrap', px: 2.5 }}
            >
              {allPhoneHealth.loading ? 'Fetching All…' : 'Fetch All Numbers'}
            </Button>
          </Box>

          {allPhoneHealth.error && (
            <Alert severity="error" sx={{ borderRadius: radiusPx.sm }}>
              {allPhoneHealth.error}
            </Alert>
          )}

          <TemplateDataTable
            columns={allColumns}
            data={filteredAllNumbers}
            isLoading={allPhoneHealth.loading}
            error={allPhoneHealth.error}
            enableRowSelection={false}
            getRowId={(row, index) => `${row?.phoneNumber || row?.id || index}`}
            emptyStateMessage="No phone numbers loaded. Click 'Fetch All Numbers' to query all WABA phone numbers."
          />
        </Box>
      )}

      {/* Tab 2: Template Health */}
      {currentTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: radiusPx.md,
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              {templates && templates.length > 0 ? (
                <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 320 } }}>
                  <InputLabel id="org-template-dropdown-label">Organisation Template</InputLabel>
                  <Select
                    labelId="org-template-dropdown-label"
                    label="Organisation Template"
                    value={
                      templates.find(
                        (t) =>
                          (t.wa_Id && t.wa_Id === templateNameInput) ||
                          (t.wa_id && t.wa_id === templateNameInput) ||
                          t.name === templateNameInput
                      )?.wa_Id ||
                      templates.find(
                        (t) =>
                          (t.wa_Id && t.wa_Id === templateNameInput) ||
                          (t.wa_id && t.wa_id === templateNameInput) ||
                          t.name === templateNameInput
                      )?.wa_id ||
                      templateNameInput ||
                      templates[0]?.wa_Id ||
                      templates[0]?.wa_id ||
                      templates[0]?.name ||
                      ''
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setTemplateNameInput(val);
                      handleCheckTemplateHealth(val);
                    }}
                  >
                    {templates.map((tpl) => {
                      const idVal = tpl.wa_Id || tpl.wa_id || tpl.name;
                      return (
                        <MenuItem key={idVal} value={idVal}>
                          {tpl.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : null}

              <TextField
                size="small"
                label="Template Name or ID"
                placeholder="Enter template name or ID"
                value={templateNameInput}
                onChange={(e) => setTemplateNameInput(e.target.value)}
                sx={{ flex: 1, minWidth: 220 }}
              />

              <Button
                variant="contained"
                onClick={() => handleCheckTemplateHealth()}
                disabled={templateHealth.loading || !templateNameInput.trim()}
                startIcon={
                  templateHealth.loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  height: 40,
                  bgcolor: '#059669',
                  '&:hover': { bgcolor: '#047857' },
                  whiteSpace: 'nowrap',
                  px: 2.5,
                }}
              >
                {templateHealth.loading ? 'Checking…' : 'Check Template Health'}
              </Button>
            </Box>
          </Paper>

          {templateHealth.error && (
            <Alert severity="error" sx={{ borderRadius: radiusPx.sm }}>
              {templateHealth.error}
            </Alert>
          )}

          {templateHealth.data && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: radiusPx.md,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  pb: 2,
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {templateHealth.data.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Category: <strong>{templateHealth.data.category}</strong> | Language:{' '}
                    <strong>{templateHealth.data.language}</strong>
                    {templateHealth.data.id ? ` | Meta ID: ${templateHealth.data.id}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <QualityRatingBadge rating={templateHealth.data.qualityScore} />
                  <StatusBadge status={templateHealth.data.status} />
                </Box>
              </Box>

              {templateHealth.data.rejectionReason && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: radiusPx.sm }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Rejection Reason:
                  </Typography>
                  <Typography variant="body2">{templateHealth.data.rejectionReason}</Typography>
                </Alert>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Tab 3: Webhook & Callbacks Simulator (Commented out)
      {currentTab === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          ...
        </Box>
      )}
      */}
    </Box>
  );
}
